import { UploadOutlined } from '@ant-design/icons';
import { uploadDealRoomInfoExternalFile, uploadDealRoomInfoFile } from '@app/api/deal-room-info.api';
import { getFileWithSignedUrl, uploadFile } from '@app/api/file.api';
import { BaseForm } from '@app/components/common/forms/BaseForm/BaseForm';
import { TextArea } from '@app/components/common/inputs/Input/Input';
import { Modal } from '@app/components/common/Modal/Modal';
import { Entity } from '@app/constants/enums/entites';
import { notificationController } from '@app/controllers/notificationController';
import { useAppSelector } from '@app/hooks/reduxHooks';
import { useDialog } from '@app/hooks/useDialog';
import { IDealRoomInfoFile } from '@app/interfaces/deal-room-info';
import { IFile } from '@app/interfaces/file';
import { UpdateImgResponse } from '@app/interfaces/interfaces';
import { getFileProps, handlePreview } from '@app/utils/file';
import { getReadableFileName, getTabId, normFile } from '@app/utils/utils';
import { Button, Col, Input, Row } from 'antd';
import { RcFile, UploadFile, UploadProps } from 'antd/lib/upload';
import mime from 'mime-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Radio, RadioGroup } from '../../Radio/Radio';
import { ChooseFileModal } from '../ChooseFileModal/ChooseFileModal';
import * as S from './DealRoomUploadFileModal.styles';

interface DealRoomUploadFileModalProps {
  dealRoomFile?: IDealRoomInfoFile | null;
  dealRoomId: string;
  open: boolean;
  sessionId?: string;
  uniqueId?: string;
  isChat?: boolean;
  channelId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export const DealRoomUploadFileModal: React.FC<DealRoomUploadFileModalProps> = ({
  dealRoomFile,
  dealRoomId,
  open,
  sessionId,
  uniqueId,
  isChat = false,
  channelId,
  onSuccess,
  onCancel,
}) => {
  const user = useAppSelector((state) => state.user.user);
  const [form] = BaseForm.useForm();
  const { t } = useTranslation();
  const { dialogOpen, handleDialogOpen, handleDialogClose } = useDialog();

  const [loader, setLoader] = useState(false);
  const [type, setType] = useState(dealRoomFile?.link ? 'link' : 'file');
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const fileProps = getFileProps({
    maxCount: 1,
    multiple: false,
    fileList: fileList,
    setList: setFileList,
    handlePreview,
  });

  async function syncUploadFile(file: UploadProps<unknown>): Promise<UpdateImgResponse> {
    return await uploadFile(
      {
        entityId: dealRoomId,
        entity: Entity.DEAL_ROOM,
        file: (file as UploadFile).originFileObj as RcFile,
      },
      !user,
    );
  }

  async function syncDealRoomFile(_payLoad: Partial<IDealRoomInfoFile>) {
    const payload = {
      ..._payLoad,
      isChat: isChat || false,
      channel: channelId || null,
      tabId: getTabId() || null,
    };

    if (user) {
      await uploadDealRoomInfoFile(dealRoomId, payload);
    } else if (uniqueId || sessionId) {
      await uploadDealRoomInfoExternalFile(dealRoomId, {
        ...payload,
        uniqueId: uniqueId || null,
        sessionId: sessionId || null,
      });
    }

    notificationController.success({
      message: t(isChat ? 'dealRooms.form.messageAddedSuccessfully' : 'dealRooms.form.documentUploadedSuccessfully'),
    });
  }

  async function fetchImage(image: string) {
    try {
      const file = await getFileWithSignedUrl(image);
      return file;
    } catch (error) {}
  }

  const fetchImages = useCallback(
    async (field: string, dealRoomImages: string[], setList: React.Dispatch<React.SetStateAction<UploadFile[]>>) => {
      const files = [];
      for (const file of dealRoomImages) {
        try {
          const i = await fetchImage(file);
          if (i)
            files.push({
              ...i,
              url: i.signedUrl,
            });
        } catch (error) {}
      }

      const mappedFiles = files.map((file) => {
        return {
          uid: file.id,
          id: file.id,
          name: getReadableFileName(file.originalName as string),
          url: file.originalName,
          type: file.originalName ? mime.lookup(file.originalName) : '',
          showPreviewIcon: false,
        };
      });

      form.setFieldsValue({ [field]: mappedFiles });
      setList(mappedFiles as UploadFile[]);
    },
    [form],
  );

  const uploadFiles = async (values: Partial<IDealRoomInfoFile>) => {
    setLoader(true);
    try {
      const files = Array.isArray(values.file) ? values.file : values.file ? [values.file] : [];

      const allFiles = files.filter((item: UploadFile) => !item.url);

      const formFiles = Array.isArray(form.getFieldValue('file'))
        ? form.getFieldValue('file')
        : form.getFieldValue('file')
        ? [form.getFieldValue('file')]
        : [];

      const fFiles = formFiles.filter((item: UploadFile) => item.url).map((item: IFile) => item.id);

      let file = fFiles.length > 0 ? fFiles[0] : null;
      if (allFiles.length > 0) {
        const newFiles = await syncUploadFile(allFiles[0]);
        file = newFiles.id;
      }

      await syncDealRoomFile({
        id: dealRoomFile?._id || '',
        file: file || null,
        link: values.link,
        description: values.description,
      });

      onSuccess();
    } catch (error) {
      notificationController.error({
        message: (error as Error).message || t('dealRooms.form.errorUploadingDocument'),
      });
    } finally {
      setLoader(false);
    }
  };

  const onOk = () => {
    form.submit();
  };

  const openFileDialog = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    handleDialogOpen();
  };

  const onSelectFiles = useCallback(
    (selectedFiles: IFile[]) => {
      const mappedFiles = selectedFiles.map((file) => ({
        uid: file.id,
        id: file.id,
        name: file.originalName ? getReadableFileName(file.originalName) : 'Unnamed File',
        url: file.id,
        type: file.originalName ? (mime.lookup(file.originalName) as string | undefined) : undefined,
        showPreviewIcon: false,
        description: file.description,
      }));
      if (!mappedFiles) return;

      setFileList(mappedFiles);
      form.setFieldsValue({ file: mappedFiles, description: mappedFiles[0]?.description || '' });
      handleDialogClose();
    },
    [form, handleDialogClose],
  );

  useEffect(() => {
    form.setFieldValue('type', type);
  }, [form, type]);

  useEffect(() => {
    if (dealRoomFile) {
      setType(dealRoomFile.link ? 'link' : 'file');
      if (dealRoomFile?.file) {
        fetchImages('file', [(dealRoomFile?.file as IFile)?.id as string], setFileList);
      }
      form.setFieldsValue({
        description: dealRoomFile?.description || '',
        link: dealRoomFile?.link || '',
      });
    }
  }, [form, dealRoomFile, fetchImages]);

  return (
    <Modal
      size="large"
      title={t('dealRooms.form.uploadDocuments')}
      open={open}
      onOk={onOk}
      onCancel={onCancel}
      okText={t('dealRooms.form.upload')}
      cancelText={t('common.cancel')}
      confirmLoading={loader}
    >
      {dialogOpen && type === 'file' && (
        <ChooseFileModal
          loading={loader}
          isOpen={dialogOpen}
          onOpenChange={handleDialogClose}
          onSelectFiles={onSelectFiles}
          multiple={false}
          isImage={false}
        />
      )}
      <BaseForm form={form} layout="vertical" name="dealRoomUploadDocuments" onFinish={uploadFiles}>
        <Row gutter={{ xs: 10, md: 15, xl: 30 }}>
          <Col span={24}>
            <BaseForm.Item
              name="type"
              label={t('dealRooms.form.type')}
              rules={[{ required: true, message: t('dealRooms.form.typeRequired') }]}
            >
              <RadioGroup onChange={(e) => setType(e.target.value)}>
                <Radio value="file">{t('dealRooms.form.file')}</Radio>
                <Radio value="link">{t('dealRooms.form.link')}</Radio>
              </RadioGroup>
            </BaseForm.Item>
          </Col>

          {type === 'link' && (
            <Col span={24}>
              <BaseForm.Item
                className="dyno-ltr"
                name="link"
                label={t('dealRooms.form.link')}
                rules={[
                  { required: true, message: t('dealRooms.form.linkRequired') },
                  { type: 'url', message: t('dealRooms.form.linkMustBeValid') },
                ]}
              >
                <Input />
              </BaseForm.Item>
            </Col>
          )}

          {type === 'file' && (
            <Col span={24}>
              <S.DraggerWrapper>
                <BaseForm.Item
                  name="file"
                  label={t('dealRooms.form.file')}
                  rules={[{ required: true, message: t('dealRooms.form.fileRequired') }]}
                >
                  <BaseForm.Item name="file" valuePropName="setFileList" getValueFromEvent={normFile}>
                    <S.UploadWrapper name="file" {...fileProps}>
                      <Button type="default" icon={<UploadOutlined />}>
                        {t('dealRooms.form.clickToUpload')}
                      </Button>
                      {user && (
                        <Button type="default" icon={<UploadOutlined />} onClick={(e) => openFileDialog(e)}>
                          {t('dealRooms.form.chooseFromFiles')}
                        </Button>
                      )}
                    </S.UploadWrapper>
                  </BaseForm.Item>
                </BaseForm.Item>
              </S.DraggerWrapper>
            </Col>
          )}

          <Col span={24}>
            <BaseForm.Item
              name="description"
              label={t('dealRooms.form.description')}
              rules={[{ required: true, message: t('dealRooms.form.descriptionRequired') }]}
            >
              <TextArea rows={2} />
            </BaseForm.Item>
          </Col>
        </Row>
      </BaseForm>
    </Modal>
  );
};
